import type { QueryRowType, Transaction } from "@rocicorp/zero";
import { z } from "zod/v4";

import { Scope } from "../../auth/scope";
import { ScopePolicy } from "../../auth/scope/policy";
import { createId } from "../../utils";
import { defineQuery } from "../../zero/transformer";
import { zql } from "../../zero/schema";

export namespace MealSchedule {
  export const DEFAULTS = [
    {
      name: "Breakfast / Lunch / Dinner",
      slots: [
        { name: "Breakfast", sortIndex: "a0", time: Temporal.PlainTime.from("08:00") },
        { name: "Lunch", sortIndex: "b0", time: Temporal.PlainTime.from("12:00") },
        { name: "Dinner", sortIndex: "c0", time: Temporal.PlainTime.from("18:30") },
      ],
    },
    {
      name: "Brunch / Dinner",
      slots: [
        { name: "Brunch", sortIndex: "a0", time: Temporal.PlainTime.from("10:30") },
        { name: "Dinner", sortIndex: "b0", time: Temporal.PlainTime.from("18:30") },
      ],
    },
  ] as const;

  export const byScope = defineQuery(
    z.object({ scopeId: z.uuid() }),
    ({ ctx, args: { scopeId } }) =>
      zql.mealSchedule
        .where("scopeId", "=", scopeId)
        .whereExists(
          "scope",
          (scope) =>
            scope.where((candidate) =>
              ScopePolicy.viewable(candidate, ctx.actor, ["user", "space"]),
            ),
          { flip: true },
        )
        .related("slots", (slot) => slot.orderBy("sortIndex", "asc"))
        .orderBy("name", "asc"),
  );

  export type Info = QueryRowType<typeof byScope>;

  export function distinctSlotNames(schedules: readonly Info[]): string[] {
    const names = new Set<string>();
    for (const schedule of schedules) {
      for (const slot of schedule.slots) {
        const name = slot.name.trim();
        if (name) names.add(name);
      }
    }
    return [...names].sort((left, right) => left.localeCompare(right));
  }

  export async function ensureDefaults(tx: Transaction, scopeId: string): Promise<void> {
    await Scope.byId(tx, scopeId, ["user", "space"]);
    const existing = await tx.run(
      zql.mealSchedule
        .where("scopeId", "=", scopeId)
        .related("slots", (slot) => slot.orderBy("sortIndex", "asc")),
    );
    const existingByName = new Map(existing.map((schedule) => [schedule.name, schedule]));
    const timeNow = Temporal.Now.instant();

    for (const defaultSchedule of DEFAULTS) {
      let schedule: (typeof existing)[number] | undefined = existingByName.get(
        defaultSchedule.name,
      );
      if (!schedule) {
        const id = createId();
        await tx.mutate.mealSchedule.insert({
          id,
          scopeId,
          name: defaultSchedule.name,
          timeCreated: timeNow,
          timeUpdated: timeNow,
        });
        schedule = {
          id,
          scopeId,
          name: defaultSchedule.name,
          timeCreated: timeNow,
          timeUpdated: timeNow,
          slots: [],
        } as (typeof existing)[number];
      }

      if (!schedule) {
        throw new Error("Failed to create or find default meal schedule");
      }

      const existingSlotNames = new Set(schedule.slots.map((slot) => slot.name));
      await Promise.all(
        defaultSchedule.slots
          .filter((slot) => !existingSlotNames.has(slot.name))
          .map((slot) =>
            tx.mutate.mealScheduleSlot.insert({
              id: createId(),
              mealScheduleId: schedule.id,
              name: slot.name,
              sortIndex: slot.sortIndex,
              time: slot.time,
              timeCreated: timeNow,
              timeUpdated: timeNow,
            }),
          ),
      );
    }
  }

  export async function requireByScope(
    tx: Transaction,
    scopeId: string,
    id: string,
  ): Promise<Info> {
    const schedule = await tx.run(
      zql.mealSchedule
        .where("scopeId", "=", scopeId)
        .where("id", "=", id)
        .related("slots", (slot) => slot.orderBy("sortIndex", "asc"))
        .one(),
    );
    if (!schedule) {
      throw new Error("Meal schedule not found for scope");
    }
    return schedule;
  }

  export async function firstDefaultForScope(tx: Transaction, scopeId: string): Promise<Info> {
    await ensureDefaults(tx, scopeId);
    const [defaultSchedule] = DEFAULTS;
    const schedule = await tx.run(
      zql.mealSchedule
        .where("scopeId", "=", scopeId)
        .where("name", "=", defaultSchedule.name)
        .related("slots", (slot) => slot.orderBy("sortIndex", "asc"))
        .one(),
    );
    if (!schedule) {
      throw new Error("Default meal schedule not found for scope");
    }
    return schedule;
  }
}
